import { Component } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import PageContainer from "src/components/PageContainer";
import { reportError } from "src/handlers/errorReporter";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Phase J1: route React errors through the single reporter so downstream
    // listeners (Sentry/Posthog/etc.) see them alongside window.onerror
    // events. The original error is still preserved for devtools.
    reportError("react.errorBoundary", {
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: errorInfo?.componentStack
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  // besides resetting the boundary we also send the user back to
  // the app root so they don't stay stuck on a broken route.
  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.assign("/");
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <PageContainer variant="narrow" role="alert" aria-live="assertive">
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 800 }}>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {this.state.error?.message || "An unexpected error occurred."}
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button variant="contained" onClick={this.handleReset}>
                Try again
              </Button>
              <Button variant="outlined" onClick={this.handleGoHome}>
                Go to home
              </Button>
            </Stack>
          </Box>
        </PageContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
